import { reactive, ref } from 'vue';
import type { OrganisationStakeholder } from '../types';

// --- Internal types ---

export type ProjectOrganisationExternalMode =
  | 'normal'
  | 'edit'
  | 'saving'
  | 'read-only';

export interface StakeholderState extends OrganisationStakeholder {
  isNew: boolean;
}

export interface InternalOrganisation {
  name: string;
  stakeholders: StakeholderState[];
}

// --- Dispatch action union ---

export type ProjectOrganisationExternalDispatchAction =
  | {
      type: 'set-organisation';
      name: string;
      stakeholders: OrganisationStakeholder[];
    }
  | { type: 'set-mode'; mode: ProjectOrganisationExternalMode }
  | { type: 'take-snapshot' }
  | { type: 'restore-snapshot' }
  | { type: 'submit-new' }
  | { type: 'delete-stakeholder'; id: string }
  | { type: 'move-up'; id: string }
  | { type: 'move-down'; id: string }
  | { type: 'update-stakeholder'; id: string; field: string; value: string };

// --- Factory ---

interface CreateProjectOrganisationExternalStoreInput {
  name: string;
  stakeholders: OrganisationStakeholder[];
  initialMode: ProjectOrganisationExternalMode;
}

export const createProjectOrganisationExternalStore = ({
  name: initialName,
  stakeholders: initialStakeholders,
  initialMode,
}: CreateProjectOrganisationExternalStoreInput) => {
  const mode = ref<ProjectOrganisationExternalMode>(initialMode);

  const newEntry = reactive({ role: '', name: '', phone: '', email: '' });

  const organisation = reactive<InternalOrganisation>({
    name: initialName,
    stakeholders: initialStakeholders
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ ...s, isNew: false })),
  });

  let snapshot: StakeholderState[] | null = null;

  const dispatch = (
    action: ProjectOrganisationExternalDispatchAction,
  ): void => {
    switch (action.type) {
      case 'set-organisation': {
        organisation.name = action.name;
        organisation.stakeholders.splice(
          0,
          organisation.stakeholders.length,
          ...action.stakeholders
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((s) => ({ ...s, isNew: false })),
        );
        break;
      }

      case 'set-mode': {
        mode.value = action.mode;
        break;
      }

      case 'take-snapshot': {
        snapshot = organisation.stakeholders.map((s) => ({ ...s }));
        break;
      }

      case 'restore-snapshot': {
        if (snapshot) {
          organisation.stakeholders.splice(
            0,
            organisation.stakeholders.length,
            ...snapshot,
          );
        }
        snapshot = null;
        break;
      }

      case 'submit-new': {
        if (!newEntry.role.trim()) return;
        organisation.stakeholders.push({
          ...newEntry,
          id: String(Date.now()),
          order: 0,
          isNew: true,
        });
        newEntry.role = '';
        newEntry.name = '';
        newEntry.phone = '';
        newEntry.email = '';
        break;
      }

      case 'delete-stakeholder': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx !== -1) organisation.stakeholders.splice(idx, 1);
        break;
      }

      case 'move-up': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx > 0) {
          [organisation.stakeholders[idx - 1], organisation.stakeholders[idx]] =
            [
              organisation.stakeholders[idx],
              organisation.stakeholders[idx - 1],
            ];
        }
        break;
      }

      case 'move-down': {
        const idx = organisation.stakeholders.findIndex(
          (s) => s.id === action.id,
        );
        if (idx !== -1 && idx < organisation.stakeholders.length - 1) {
          [organisation.stakeholders[idx], organisation.stakeholders[idx + 1]] =
            [
              organisation.stakeholders[idx + 1],
              organisation.stakeholders[idx],
            ];
        }
        break;
      }

      case 'update-stakeholder': {
        const s = organisation.stakeholders.find((s) => s.id === action.id);
        if (s) (s as Record<string, unknown>)[action.field] = action.value;
        break;
      }
    }
  };

  return {
    organisation,
    mode,
    newEntry,
    get snapshot() {
      return snapshot;
    },
    dispatch,
  };
};
